import React from 'react';

const Privacy: React.FC = () => {
    return (
        <section className="hero-section terms-section text-center text-white">
          <h1 className="hero-headline">Privacy Policy</h1>
          <div className="hero-paragraph text-start mx-auto" style={{ maxWidth: '760px' }}>
            <p className="legal-effective">Version 1.0 — effective 12 September 2026</p>
            <p>
              VoyCalc AS is committed to handling personal data lawfully and transparently. This policy
              explains what we collect, why, and what rights you have.
            </p>

            <h2 className="mt-5 h4">1. Who we are</h2>
            <p>
              <strong>VoyCalc AS</strong><br />
              Org. no. 938 332 959<br />
              Lindbackveien 9D, 1163 Oslo, Norway<br />
              <a href="mailto:privacy@voycalc.com" className="text-white">privacy@voycalc.com</a>
            </p>
            <p>
              For the processing described in this policy, VoyCalc AS is the <strong>data controller</strong>,
              unless stated otherwise in section 3.
            </p>

            <h2 className="mt-5 h4">2. Scope</h2>
            <p>This policy covers:</p>
            <ul>
              <li>Visitors to <strong>voycalc.com</strong></li>
              <li>People who submit the <strong>Register Interest</strong> form</li>
              <li><strong>Authorised users</strong> of the VoyCalc platform</li>
              <li>Business contacts at customers, prospects and partners</li>
            </ul>

            <h2 className="mt-5 h4">3. Where we are not the controller</h2>
            <p>
              The VoyCalc platform is provided to business customers. Data that a customer enters into its own
              workspace — vessels, ports, estimates, counterparties, and any contact details within them — is
              processed by us <strong>on that customer's behalf, as a processor</strong>. The customer decides
              why and how that data is used.
            </p>
            <p>
              If you are an employee of one of our customers and want to know how your employer uses the
              platform, contact your employer. That processing is governed by our Data Processing Agreement
              with them, not by this policy.
            </p>

            <h2 className="mt-5 h4">4. What we collect and why</h2>

            <h3 className="mt-4 h5">4.1 Register Interest form</h3>
            <table className="legal-table">
              <tbody>
                <tr><th>Data</th><td>Email address, company name, job role, free-text comment</td></tr>
                <tr><th>Purpose</th><td>To respond to your enquiry, arrange a demonstration, and assess fit</td></tr>
                <tr><th>Legal basis</th><td>Legitimate interest in responding to a business enquiry (GDPR art. 6(1)(f)), and steps prior to entering a contract (art. 6(1)(b))</td></tr>
                <tr><th>Retention</th><td>24 months from last contact, unless you become a customer</td></tr>
              </tbody>
            </table>
            <p>The comment field is free text. Please do not enter sensitive personal information in it.</p>

            <h3 className="mt-4 h5">4.2 Platform user accounts</h3>
            <table className="legal-table">
              <tbody>
                <tr><th>Data</th><td>Name, business email, job title, employer, account credentials (passwords stored hashed), authentication and access logs, IP address</td></tr>
                <tr><th>Purpose</th><td>To create and administer accounts, authenticate users, secure the service, and investigate incidents</td></tr>
                <tr><th>Legal basis</th><td>Performance of the contract with the customer (art. 6(1)(b)); legitimate interest in securing the service (art. 6(1)(f))</td></tr>
                <tr><th>Retention</th><td>For the duration of the account, plus 3 months for security logs</td></tr>
              </tbody>
            </table>

            <h3 className="mt-4 h5">4.3 Website technical data</h3>
            <table className="legal-table">
              <tbody>
                <tr><th>Data</th><td>IP address, browser type, requested pages and timestamps in technical request logs; an anonymous identifier used by our map component on the route and weather data pages</td></tr>
                <tr><th>Purpose</th><td>To operate and secure the website, and to display the maps you request</td></tr>
                <tr><th>Legal basis</th><td>Legitimate interest in operating a secure website (art. 6(1)(f))</td></tr>
                <tr><th>Retention</th><td>Request logs: up to 1 month. The map identifier is refreshed at least every 24 hours</td></tr>
              </tbody>
            </table>
            <p>
              See our <a href="/?page=cookies" className="text-white">Cookie Policy</a> for detail on local
              storage and the map component.
            </p>

            <h3 className="mt-4 h5">4.4 Business contacts</h3>
            <table className="legal-table">
              <tbody>
                <tr><th>Data</th><td>Name, business email, phone, company, role</td></tr>
                <tr><th>Purpose</th><td>Customer relationship management, contract administration, invoicing</td></tr>
                <tr><th>Legal basis</th><td>Performance of a contract (art. 6(1)(b)); legitimate interest in managing business relationships (art. 6(1)(f))</td></tr>
                <tr><th>Retention</th><td>For the duration of the relationship, plus statutory accounting retention</td></tr>
              </tbody>
            </table>

            <h2 className="mt-5 h4">5. We do not</h2>
            <ul>
              <li>Sell personal data</li>
              <li>Use personal data for automated decision-making with legal effect</li>
              <li>Use customer data to train machine learning models</li>
              <li>Send marketing to individuals who have not asked to hear from us</li>
            </ul>

            <h2 className="mt-5 h4">6. Who we share data with</h2>
            <p>We use the following processors:</p>
            <table className="legal-table">
              <thead>
                <tr><th>Processor</th><th>Purpose</th><th>Location</th></tr>
              </thead>
              <tbody>
                <tr><td>Amazon Web Services EMEA SARL</td><td>Hosting, storage, database, API, and transactional / enquiry e-mail (Amazon SES)</td><td>Ireland (eu-west-1), or a region agreed with the customer</td></tr>
                <tr><td>Mapbox, Inc.</td><td>Map rendering on the website's route and weather data pages and in the platform</td><td>United States</td></tr>
                <tr><td>Accounting and bookkeeping provider</td><td>Invoicing and statutory bookkeeping</td><td>Norway</td></tr>
              </tbody>
            </table>
            <p>We may also disclose data to advisers, auditors or authorities where required by law.</p>

            <h2 className="mt-5 h4">7. Transfers outside the EEA</h2>
            <p>
              Our infrastructure is hosted in the EEA by default. Where a processor is located outside the
              EEA — for example our map provider — transfers are made under the <strong>Standard Contractual
              Clauses</strong> adopted by the European Commission, together with any additional safeguards required.
            </p>
            <p>
              Where a customer has agreed a hosting region outside the EEA, that transfer takes place on the
              customer's instruction and under the same clauses.
            </p>

            <h2 className="mt-5 h4">8. Security</h2>
            <p>
              We apply technical and organisational measures appropriate to the risk, including encryption in
              transit and at rest, role-based access control, multi-factor authentication for administrative
              access, audit logging, and separation of each customer into a dedicated cloud account. Measures
              are reviewed and updated as the service develops.
            </p>

            <h2 className="mt-5 h4">9. Your rights</h2>
            <p>
              You have the right to request access to your personal data, correction of inaccurate data,
              deletion, restriction of processing, portability, and to object to processing based on
              legitimate interest. Where processing is based on consent, you may withdraw it at any time.
            </p>
            <p>
              To exercise any of these, contact{' '}
              <a href="mailto:privacy@voycalc.com" className="text-white">privacy@voycalc.com</a>.
              We respond within one month.
            </p>
            <p>
              If you are dissatisfied, you may complain to the Norwegian Data Protection Authority
              (Datatilsynet), Postboks 458 Sentrum, 0105 Oslo,{' '}
              <a href="https://www.datatilsynet.no" className="text-white" target="_blank" rel="noreferrer">datatilsynet.no</a>,
              or to the supervisory authority in your own country.
            </p>

            <h2 className="mt-5 h4">10. Changes</h2>
            <p>
              We may update this policy. The current version and its effective date are always shown at the
              top of this page. Material changes affecting platform users will be notified to the relevant
              customer.
            </p>

            <p className="legal-company mt-5">
              VoyCalc AS — org. no. 938 332 959 — Lindbackveien 9D, 1163 Oslo, Norway
            </p>
          </div>
        </section>
    );
}

export default Privacy
